import Settings from '@/components/buy-stack/ProductSelectSettings';
import {
  HelperOption,
  createHelperOptions,
} from '@/experiments/product-select-v3/helper-options';
import {DeliveryOption} from '@/product/delivery-option';

/**
 * Renders a list of delivery options as a frequency helper selector.
 * The title of each button is a mapped value from the `frequency_helper_options` setting.
 * This allows us to customize the text of each button, while mapping onto the delivery options.
 * The title of the form is a mapped value from the `frequency_helper_title` setting.
 */
export default function FrequencyHelperSelector({
  options,
  onUpdate,
  value,
}: FrequencyHelperSelectorProps) {
  const handleSelectOption = ({frequency, unit}: HelperOption) => {
    const deliveryOption = options.find(
      ({sellingPlan}) =>
        sellingPlan?.orderIntervalFrequency === frequency &&
        sellingPlan?.intervalUnit === unit
    );
    if (deliveryOption && onUpdate) {
      onUpdate(deliveryOption);
    }
  };

  return (
    <Settings.Consumer>
      {({frequency_helper_title, frequency_helper_options}) => {
        const helperOptions = frequency_helper_options
          ? createHelperOptions(frequency_helper_options, options)
          : [];

        return (
          <form
            className="flex flex-col items-center gap-y-4 text-center"
            onSubmit={(e) => e.preventDefault()}
          >
            <h6 className="text-base font-semibold text-neutral-dark-gray">
              {frequency_helper_title}
            </h6>
            <div className="grid w-full max-w-52 grid-cols-1 gap-3 md:max-w-none md:grid-cols-3">
              {helperOptions.map((option) => {
                const {title, description, unit, frequency} = option;
                const selectedPlan = value?.sellingPlan;
                const isSelected =
                  selectedPlan?.orderIntervalFrequency === frequency &&
                  selectedPlan?.intervalUnit === unit;
                return (
                  <button
                    className="relative z-10 flex w-full flex-col items-center rounded border-2 border-primary-blue px-2 py-1.5 text-sm text-neutral-dark-gray"
                    key={title}
                    onClick={() => handleSelectOption(option)}
                  >
                    {isSelected && (
                      <div className="absolute inset-0 -z-10 bg-primary-blue/20"></div>
                    )}
                    <span>{title}</span>
                    <span className="text-xs font-normal">{description}</span>
                  </button>
                );
              })}
            </div>
          </form>
        );
      }}
    </Settings.Consumer>
  );
}

export type FrequencyHelperSelectorProps = {
  onUpdate?: (value: DeliveryOption) => void;
  options: DeliveryOption[];
  value?: DeliveryOption | null;
};
